import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Feedback";
import { TextField } from "@/components/ui/Field";
import { ApiError } from "@/lib/api";
import { enableTotp, startTotpSetup } from "@/lib/auth-api";

/** Generates a new authenticator seed, shows its QR code and confirms it with a first code. */
export function TotpSetupPanel({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const setup = useMutation({ mutationFn: startTotpSetup });
  const enable = useMutation({ mutationFn: () => enableTotp(code), onSuccess: onDone });
  const { mutate: start } = setup;
  const started = useRef(false);

  useEffect(() => {
    // One seed per panel, even when React runs effects twice in development.
    if (started.current) return;
    started.current = true;
    start();
  }, [start]);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    enable.mutate();
  };

  return (
    <div className="space-y-4">
      {setup.isError && <Alert>{t("common.errorGeneric")}</Alert>}
      {setup.data && (
        <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
          <div
            className="size-44 shrink-0 rounded-control border border-line bg-white p-1 [&>svg]:size-full"
            role="img"
            aria-label={t("auth.setupTitle")}
            // Generated server-side by the QR library from our own otpauth URI; contains only path data.
            dangerouslySetInnerHTML={{ __html: setup.data.qr_svg }}
          />
          <div className="min-w-0 text-sm text-ink-muted">
            <p>{t("auth.setupManual")}</p>
            <code className="mt-2 block rounded-control bg-sunken px-3 py-2 font-medium tracking-wider break-all text-ink">
              {setup.data.secret.match(/.{1,4}/g)?.join(" ")}
            </code>
          </div>
        </div>
      )}
      <form onSubmit={onSubmit} className="space-y-4">
        {enable.error instanceof ApiError && <Alert>{t("auth.invalidCode")}</Alert>}
        <TextField
          label={t("auth.code")}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          pattern="[0-9]{6}"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          required
        />
        <Button type="submit" busy={enable.isPending} disabled={!setup.data || code.length !== 6}>
          {t("auth.activate")}
        </Button>
      </form>
    </div>
  );
}
